import { create } from 'zustand';

/**
 * Row type discriminator for the site table
 */
export type RowType = 'plot' | 'labourItem' | 'variation' | 'allocation';

/**
 * Site table UI state
 */
interface SiteTableState {
  /** Currently selected row ID (for highlighting and panel integration) */
  selectedRowId: string | null;

  /** Type of the currently selected row */
  selectedRowType: RowType | null;

  /** Current site ID for the table */
  siteId: number | null;
}

/**
 * Site table actions
 */
interface SiteTableActions {
  /** Set the selected row */
  setSelectedRow: (id: string | null, type: RowType | null) => void;

  /** Clear the selection */
  clearSelection: () => void;

  /** Set the site ID */
  setSiteId: (siteId: number | null) => void;
}

/**
 * Site table store for managing table UI state
 *
 * Handles row selection state for performance optimization with React.memo.
 * Each row component subscribes with a selector to only re-render when its
 * own selection state changes.
 */
export const useSiteTableStore = create<SiteTableState & SiteTableActions>(
  set => ({
    // Initial state
    selectedRowId: null,
    selectedRowType: null,
    siteId: null,

    // Actions
    setSelectedRow: (id, type) =>
      set({ selectedRowId: id, selectedRowType: type }),
    clearSelection: () => set({ selectedRowId: null, selectedRowType: null }),
    setSiteId: siteId => set({ siteId }),
  })
);
